import { useEffect, useRef, useState } from "react";

export interface UseLazyLoadItemsOptions {
  /** Items per batch to load on scroll */
  itemsPerPage?: number;
  /** Threshold in pixels from bottom to trigger load */
  scrollThreshold?: number;
}

/**
 * Hook for lazy loading items on scroll.
 * Efficiently renders large lists by only displaying visible items.
 *
 * @param totalItems Total number of items to render
 * @param options Configuration options
 * @returns Object with containerRef, itemsToDisplay, and hasMore flag
 *
 * @example
 * const { containerRef, itemsToDisplay } = useLazyLoadItems(1000, {
 *   itemsPerPage: 20,
 * });
 *
 * return (
 *   <div ref={containerRef} style={{ maxHeight: "600px", overflow: "auto" }}>
 *     {data.slice(0, itemsToDisplay).map(item => <ItemComponent key={item.id} item={item} />)}
 *   </div>
 * );
 */
export function useLazyLoadItems(
  totalItems: number,
  options: UseLazyLoadItemsOptions = {}
) {
  const { itemsPerPage = 20, scrollThreshold = 2 } = options;

  const [numberOfItemsToDisplay, setNumberOfItemsToDisplay] =
    useState(itemsPerPage);
  const [measuredItemHeight, setMeasuredItemHeight] = useState<number | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollListenerRef = useRef<(() => void) | null>(null);

  // Measure first item height on mount
  useEffect(() => {
    const measureHeight = () => {
      const container = containerRef.current;
      if (container && container.firstChild) {
        const firstChild = container.firstChild as HTMLElement;
        const height = firstChild.offsetHeight;
        if (height > 0) {
          setMeasuredItemHeight(height);
          return true;
        }
      }
      return false;
    };

    if (!measuredItemHeight) {
      if (measureHeight()) return;
      const timeoutId = requestAnimationFrame(measureHeight);
      return () => cancelAnimationFrame(timeoutId);
    }
  }, [measuredItemHeight]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Clean up previous listener if it exists
    if (scrollListenerRef.current) {
      container.removeEventListener("scroll", scrollListenerRef.current);
    }

    const handleScroll = () => {
      if (!container) return;

      const scrollPosition = container.scrollTop + container.clientHeight;
      const scrollThresholdPixels = container.clientHeight * scrollThreshold;
      const isNearBottom =
        scrollPosition >= container.scrollHeight - scrollThresholdPixels;

      if (isNearBottom && numberOfItemsToDisplay < totalItems) {
        setNumberOfItemsToDisplay((prev) =>
          Math.min(prev + itemsPerPage, totalItems)
        );
      }
    };

    scrollListenerRef.current = handleScroll;
    container.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollListenerRef.current) {
        container.removeEventListener("scroll", scrollListenerRef.current);
      }
    };
  }, [numberOfItemsToDisplay, totalItems, itemsPerPage, scrollThreshold]);

  const maxHeight = measuredItemHeight
    ? `${measuredItemHeight * 20}px`
    : undefined;

  return {
    containerRef,
    itemsToDisplay: numberOfItemsToDisplay,
    hasMore: numberOfItemsToDisplay < totalItems,
    itemHeight: measuredItemHeight,
    maxHeight,
  };
}
